import * as React from 'react';

import InputMask from 'react-input-mask';
import { NumericFormat } from 'react-number-format';
import { twMerge } from 'tailwind-merge';

import { FloatingLabel } from '../floating-label';
import { IInputProps, TCommonInputProps } from './types';

const CommonInput = React.forwardRef<HTMLInputElement, TCommonInputProps>(
  ({ size, placeholder, className, isCustomPlaceholder, ...rest }, ref) => {
    return (
      <input
        ref={ref}
        {...rest}
        className={twMerge(
          'h-5 w-full flex-1 select-none appearance-none overflow-hidden border-none bg-transparent p-0 text-[16px] shadow-none outline-none ring-0 file:border-0 file:bg-surface-00-transparent file:text-sm file:font-medium file:text-base-500',
          size !== 'sm' && 'placeholder:text-transparent',
          className,
        )}
        placeholder={placeholder}
      />
    );
  },
);
const Input = React.forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      id,
      className,
      error,
      isInvalid,
      onValueChange,
      onChange,
      placeholder,
      customPlaceholder,
      afterIcon,
      description,
      variant = 'default',
      size = 'default',
      mask,
      maskInputProps = {},
      isNumber,
      numberInputProps = { decimalScale: 2, allowNegative: false },
      beforeIcon,
      disabled,
      classNames = {},
      value,
      inputClassName,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(
      ref,
      () => inputRef.current!,
    );

    const focusInputHandler = (event: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);
      setIsFocused(true);
    };

    const blurInputHandler = (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      setIsFocused(false);
    };
    const changeValueHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange?.(event.currentTarget.value);
      onChange?.(event);
    };

    return (
      <FloatingLabel
        isFocused={isFocused || Boolean(value)}
        placeholder={placeholder ?? ''}
        customPlaceholder={customPlaceholder}
        variant={variant}
        isInvalid={isInvalid || Boolean(error)}
        disabled={disabled}
        className={className ?? ''}
        description={description ?? ''}
        endContent={afterIcon}
        size={size}
        onClick={() => {
          if (!disabled) {
            inputRef.current?.focus();
          }
        }}
        classNames={{
          wrapper: classNames.wrapper,
          description: classNames.description,
          placeholder: classNames.placeholder,
        }}
      >
        {beforeIcon}

        {mask ? (
          <InputMask
            inputRef={inputRef}
            {...props}
            {...maskInputProps}
            className={twMerge(
              'peer flex-1 appearance-none border-none bg-transparent px-0 py-0 text-[16px] shadow-none outline-none ring-0 file:border-0 file:bg-surface-00-transparent file:text-sm file:font-medium file:text-base-500',
              size !== 'sm' &&
              !customPlaceholder &&
              'placeholder:text-transparent',
              classNames.input,
            )}
            value={value}
            mask={mask}
            onFocus={focusInputHandler}
            onBlur={blurInputHandler}
            placeholder={placeholder}
            formatChars={
              maskInputProps.formatChars ?? {
                '#': '[0-9]',
                '?': '[0-9]',
              }
            }
            onChange={changeValueHandler}
            maskChar={maskInputProps.maskChar ?? '_'}
          />
        ) : isNumber ? (
          <NumericFormat
            {...props}
            {...numberInputProps}
            allowedDecimalSeparators={
              numberInputProps?.allowedDecimalSeparators ?? ['.', ',']
            }
            decimalScale={
              numberInputProps?.decimalScale === undefined
                ? 2
                : numberInputProps?.decimalScale
            }
            inputMode={
              numberInputProps?.inputMode ??
              (numberInputProps?.decimalScale === 0 ? 'numeric' : 'decimal')
            }
            value={value}
            thousandSeparator={numberInputProps?.thousandSeparator ?? ' '}
            allowNegative={numberInputProps?.allowNegative ?? false}
            allowLeadingZeros={numberInputProps?.allowLeadingZeros ?? false}
            disabled={disabled}
            getInputRef={inputRef}
            onFocus={focusInputHandler}
            onBlur={blurInputHandler}
            placeholder={placeholder || customPlaceholder}
            customInput={CommonInput}
            className={inputClassName}
            isCustomPlaceholder={Boolean(customPlaceholder)}
            onValueChange={({ value }) => {
              onValueChange?.(value);
            }}
          />
        ) : (
          <CommonInput
            ref={inputRef}
            {...props}
            value={value}
            disabled={disabled}
            className={inputClassName}
            onChange={changeValueHandler}
            size={size}
            onFocus={focusInputHandler}
            onBlur={blurInputHandler}
            isCustomPlaceholder={Boolean(customPlaceholder)}
            placeholder={placeholder || customPlaceholder}
          />
        )}
      </FloatingLabel>
    );
  },
);

export { Input };
